import { Link } from "react-router-dom";
import { ProductCard } from "@/components/product/ProductCard";
import type { ProductListItem } from "@/lib/api-client";

export interface ProductGridProps {
  title: string;
  viewAllHref: string;
  /** 10 produits attendus (grille PC 2 lignes × 5 colonnes). */
  products: ProductListItem[];
}

/**
 * Grille produits de l'accueil — toujours 2 lignes, nombre de colonnes selon l'écran :
 *
 *   - ordinateur (≥ 1024px) : 5 colonnes × 2 lignes = 10 produits ;
 *   - tablette (768–1023px) : 4 colonnes × 2 lignes = 8 produits ;
 *   - mobile (< 768px)      : 2 colonnes × 2 lignes = 4 produits.
 *
 * Les produits en trop pour l'écran courant sont masqués en CSS (pas de requête
 * différente par écran) : on charge les 10 plus récents une seule fois.
 *
 * Largeur plafonnée (`max-w-6xl`, centrée) : cartes compactes (~210px) sur PC.
 */
export function ProductGrid({ title, viewAllHref, products }: ProductGridProps) {
  if (products.length === 0) return null;

  return (
    <section className="container-vvs py-8 lg:py-10">
      <div className="mx-auto max-w-6xl">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <span aria-hidden="true" className="inline-block h-7 w-1 rounded-sm bg-gold" />
            <h2 className="font-display text-xl font-semibold text-navy lg:text-2xl">{title}</h2>
          </div>
          <Link to={viewAllHref} className="font-body text-sm font-medium text-golddark hover:text-gold2">
            Voir tout →
          </Link>
        </div>

        <ul className="mt-4 grid grid-cols-2 gap-3 sm:gap-4 md:grid-cols-4 lg:grid-cols-5 lg:gap-5">
          {products.slice(0, 10).map((product, index) => (
            <li key={product.id} className={visibilityClass(index)}>
              <ProductCard product={product} />
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

/** Mobile : 4 premiers ; tablette : 8 premiers ; PC : les 10. */
function visibilityClass(index: number): string | undefined {
  if (index >= 8) return "hidden lg:block";
  if (index >= 4) return "hidden md:block";
  return undefined;
}
